// Pure URL/line parsing for the UTM Health Snapshot paste + CSV flows.
// Zero DOM / Node deps so the same code runs on the Worker + in the browser.

import type { UtmRow } from "./types";

/** Split a pasted blob into lines, trim, skip blanks + comments. */
export function splitLines(input: string): string[] {
  return input
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0 && !l.startsWith("#"));
}

/** Parse a single full URL into a UtmRow. Returns null if not a URL. */
export function parseUrl(raw: string): UtmRow | null {
  let url: URL;
  try {
    url = new URL(raw.startsWith("http") ? raw : `https://${raw}`);
  } catch {
    return null;
  }
  const p = url.searchParams;
  const get = (k: string) => p.get(k) ?? undefined;
  return {
    url: url.toString(),
    source: get("utm_source"),
    medium: get("utm_medium"),
    campaign: get("utm_campaign"),
    content: get("utm_content"),
    term: get("utm_term"),
    sourcePlatform: get("utm_source_platform"),
  };
}

/** Parse either URLs or key=value blocks pasted into the textarea. */
export function parsePasted(input: string): UtmRow[] {
  const lines = splitLines(input);
  const rows: UtmRow[] = [];
  for (const line of lines) {
    const row = parseUrl(line);
    if (row) rows.push(row);
  }
  return rows;
}

/**
 * Map a CSV row (headers lower-cased, trimmed) into a UtmRow.
 * Accepts either a full URL column, or explicit utm_* columns.
 * Unknown columns are ignored.
 */
export function csvRowToUtm(record: Record<string, string>, headers: string[]): UtmRow | null {
  const lookup: Record<string, string | undefined> = {};
  for (const h of headers) lookup[h.trim().toLowerCase()] = record[h]?.trim();

  const urlVal = lookup["url"] ?? lookup["final url"] ?? lookup["landing url"];
  if (urlVal) {
    const viaUrl = parseUrl(urlVal);
    if (viaUrl) {
      // Merge with explicit columns if both were provided — explicit wins
      // (matches how Sked ad platforms attach UTMs alongside landing URLs).
      return {
        ...viaUrl,
        source: lookup["utm_source"] ?? viaUrl.source,
        medium: lookup["utm_medium"] ?? viaUrl.medium,
        campaign: lookup["utm_campaign"] ?? viaUrl.campaign,
        content: lookup["utm_content"] ?? viaUrl.content,
        term: lookup["utm_term"] ?? viaUrl.term,
        sourcePlatform: lookup["utm_source_platform"] ?? viaUrl.sourcePlatform,
        sessions: parseSessions(lookup["sessions"] ?? lookup["clicks"]),
      };
    }
  }

  // No URL column — try to assemble from explicit utm_* fields.
  if (
    lookup["utm_source"] ||
    lookup["utm_medium"] ||
    lookup["utm_campaign"] ||
    lookup["utm_content"]
  ) {
    return {
      source: lookup["utm_source"],
      medium: lookup["utm_medium"],
      campaign: lookup["utm_campaign"],
      content: lookup["utm_content"],
      term: lookup["utm_term"],
      sourcePlatform: lookup["utm_source_platform"],
      sessions: parseSessions(lookup["sessions"] ?? lookup["clicks"]),
    };
  }
  return null;
}

function parseSessions(v: string | undefined): number | undefined {
  if (!v) return undefined;
  const n = Number(v.replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}
